/**
 * MilestonesDashboardModule — agent-only per-property progress dashboard.
 *
 * Placed on any modular page as a Module Instance. Only the signed-in agent
 * who owns properties can see/use it — everyone else sees a sign-in gate
 * (mirrors the VerifyPhotoModule auth-gate pattern, but probes the
 * property-admin agent session instead of shopper SSO).
 *
 * Once signed in, the agent picks one of their own properties from a
 * dropdown and manages that property's milestones (name, colour-coded %
 * complete, one-line note, status) directly inline — same backend used by
 * `/api/property-milestones`, which already scopes every read/write to the
 * agent's own properties via the `property_admin_token` cookie.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LayoutDashboard, Plus, Pencil, Trash2, Loader2, ListChecks, LogIn, ChevronUp, ChevronDown, Check, MessageCircle, Lock, ShieldAlert } from "lucide-react";
import WhatsAppUpdateModal from "@/components/property-admin/WhatsAppUpdateModal";

const API = getApiUrl();

const STATUS_OPTIONS = [
  { value: "not_started", label: "Not started", dot: "bg-gray-400", badge: "bg-gray-100 text-gray-700" },
  { value: "in_progress", label: "In Progress", dot: "bg-blue-500", badge: "bg-blue-100 text-blue-700" },
  { value: "blocked", label: "Blocked", dot: "bg-red-500", badge: "bg-red-100 text-red-700" },
  { value: "completed", label: "Completed", dot: "bg-green-500", badge: "bg-green-100 text-green-700" },
];

function statusMeta(value) {
  return STATUS_OPTIONS.find((s) => s.value === value) || STATUS_OPTIONS[0];
}

// Colour-coded sliding scale: red (low) -> amber (mid) -> green (high)
function percentColor(pct) {
  if (pct >= 100) return { bar: "bg-emerald-500", text: "text-emerald-700", accent: "#10b981" };
  if (pct >= 67) return { bar: "bg-green-500", text: "text-green-700", accent: "#22c55e" };
  if (pct >= 34) return { bar: "bg-amber-500", text: "text-amber-700", accent: "#f59e0b" };
  return { bar: "bg-red-500", text: "text-red-700", accent: "#ef4444" };
}

const EMPTY_FORM = { name: "", percent_complete: 0, note: "", status: "not_started" };

// A milestone whose name contains "*" is a required gate: every OTHER
// starred milestone that comes after it (in order) stays collapsed/locked
// until it is completed. Non-starred milestones are never affected.
const isRequiredMilestone = (m) => (m?.name || "").includes("*");
const displayName = (name) => (name || "").replace(/\*/g, "").trim();

export default function MilestonesDashboardModule({ config = {}, adminAgentId = null, adminProperties = null, initialPropertyId = null }) {
  const {
    heading = "My Home Dashboard",
    subheading = "Track milestones and progress for each of your homes.",
  } = config;

  // Master Admin override — when `adminAgentId` is supplied (from the Module
  // Instance admin screen), skip the agent-session probe entirely and act on
  // behalf of that agent, scoping every request with `?agent_id=`.
  const isAdminMode = !!adminAgentId;
  const agentIdParam = isAdminMode ? { agent_id: adminAgentId } : {};

  // Auth gate — probes the agent's own session. Any other visitor (signed
  // out, or signed in as a shopper/master admin only) sees the sign-in gate.
  const [authState, setAuthState] = useState({ loading: !isAdminMode, signedIn: isAdminMode });
  const [linkedProperties, setLinkedProperties] = useState(isAdminMode ? (adminProperties || []) : []);
  const [selectedPropertyId, setSelectedPropertyId] = useState(
    isAdminMode ? (adminProperties || [])[0]?.property_id || "" : ""
  );
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Which milestone is mid-save from an inline slider drag or reorder click
  // — shows a small spinner in place of that row's action icons.
  const [savingId, setSavingId] = useState(null);
  const [buyerContact, setBuyerContact] = useState(null);
  const [whatsappOpen, setWhatsappOpen] = useState(false);

  useEffect(() => {
    if (isAdminMode) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-admin/me`, { withCredentials: true });
        if (!alive) return;
        if (data.login_type === "agent" && data.agent) {
          setAuthState({ loading: false, signedIn: true, agentName: data.agent.name });
          const props = data.linked_properties || [];
          setLinkedProperties(props);
          // Preselect the property the caller opened this modal for (e.g.
          // the "Dashboard" CTA on a specific property card), falling back
          // to the first property if it's not one of this agent's own.
          const preferred = initialPropertyId && props.find((p) => p.property_id === initialPropertyId)
            ? initialPropertyId
            : props[0]?.property_id || "";
          setSelectedPropertyId(preferred);
        } else {
          setAuthState({ loading: false, signedIn: false });
        }
      } catch {
        if (alive) setAuthState({ loading: false, signedIn: false });
      }
    })();
    return () => { alive = false; };
  }, [isAdminMode]);

  // Keep admin-mode property list/selection in sync when the caller swaps
  // to a different agent.
  useEffect(() => {
    if (!isAdminMode) return;
    const props = adminProperties || [];
    setLinkedProperties(props);
    setSelectedPropertyId((prev) =>
      props.find((p) => p.property_id === prev) ? prev : (props[0]?.property_id || "")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminMode, adminAgentId, adminProperties]);

  const load = useCallback(async () => {
    if (!selectedPropertyId) {
      setMilestones([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/property-milestones`, {
        params: { property_id: selectedPropertyId, ...agentIdParam },
        withCredentials: true,
      });
      setMilestones(res.data?.milestones || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load milestones");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyId, adminAgentId]);

  useEffect(() => {
    if (authState.signedIn) load();
  }, [authState.signedIn, load]);

  // Jump straight to the first incomplete milestone whenever the dashboard
  // opens/reloads — this is the whole point of clicking the progress strip
  // ("take me to what needs doing next") rather than landing at the top.
  useEffect(() => {
    if (loading || !milestones.length) return;
    const next = milestones.find((m) => m.status !== "completed");
    if (!next) return;
    const el = document.querySelector(`[data-testid="milestone-row-${next.milestone_id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-offset-2", "ring-emerald-500");
      const timer = setTimeout(() => el.classList.remove("ring-2", "ring-offset-2", "ring-emerald-500"), 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, milestones, selectedPropertyId]);

  // Fetch buyer contact (from an offer on this property) to conditionally
  // show the "Message buyer" WhatsApp button — skipped in admin-mode since
  // that requires the agent's own signed-in session.
  useEffect(() => {
    if (isAdminMode || !authState.signedIn || !selectedPropertyId) {
      setBuyerContact(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-admin/whatsapp/buyer-contact`, {
          params: { property_id: selectedPropertyId },
          withCredentials: true,
        });
        if (alive) setBuyerContact(data?.contact || null);
      } catch {
        if (alive) setBuyerContact(null);
      }
    })();
    return () => { alive = false; };
  }, [isAdminMode, authState.signedIn, selectedPropertyId]);

  const selectedProperty = useMemo(
    () => linkedProperties.find((p) => p.property_id === selectedPropertyId),
    [linkedProperties, selectedPropertyId]
  );

  // As soon as any required ("*") milestone is incomplete, every milestone
  // AFTER it (in display order) — required or not — is disabled/collapsed.
  const requiredLockMap = useMemo(() => {
    const map = {};
    let blocked = false;
    for (const m of milestones) {
      if (blocked) map[m.milestone_id] = true;
      if (isRequiredMilestone(m) && m.status !== "completed") blocked = true;
    }
    return map;
  }, [milestones]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditingId(m.milestone_id);
    setForm({ name: m.name, percent_complete: m.percent_complete, note: m.note || "", status: m.status });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Milestone name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.patch(
          `${API}/property-milestones/${editingId}`,
          {
            name: form.name,
            percent_complete: Number(form.percent_complete),
            note: form.note,
            status: form.status,
          },
          { params: agentIdParam, withCredentials: true }
        );
        setMilestones((prev) => prev.map((m) => (m.milestone_id === editingId ? res.data : m)));
        toast.success("Milestone updated");
      } else {
        const res = await axios.post(
          `${API}/property-milestones`,
          {
            property_id: selectedPropertyId,
            name: form.name,
            percent_complete: Number(form.percent_complete),
            note: form.note,
            status: form.status,
          },
          { params: agentIdParam, withCredentials: true }
        );
        setMilestones((prev) => [...prev, res.data]);
        toast.success("Milestone added");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save milestone");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/property-milestones/${deleteTarget.milestone_id}`, {
        params: agentIdParam,
        withCredentials: true,
      });
      setMilestones((prev) => prev.filter((m) => m.milestone_id !== deleteTarget.milestone_id));
      toast.success("Milestone deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete milestone");
    } finally {
      setDeleteTarget(null);
    }
  };

  // Auto-save helper for the inline percent slider — fires once on
  // pointer/touch release (not on every drag tick) so it doesn't spam the API.
  const patchMilestone = async (milestoneId, patch) => {
    setSavingId(milestoneId);
    try {
      const res = await axios.patch(
        `${API}/property-milestones/${milestoneId}`,
        patch,
        { params: agentIdParam, withCredentials: true }
      );
      setMilestones((prev) => prev.map((m) => (m.milestone_id === milestoneId ? res.data : m)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      load();
    } finally {
      setSavingId(null);
    }
  };

  // Tick a milestone straight to 100% / Completed; unticking resets it back
  // to 0% / Not started (red) — completion here is intentionally binary.
  const toggleCompleted = (m) => {
    if (m.status === "completed") {
      patchMilestone(m.milestone_id, { status: "not_started", percent_complete: 0 });
    } else {
      patchMilestone(m.milestone_id, { status: "completed", percent_complete: 100 });
    }
  };
  const moveMilestone = async (milestoneId, direction) => {
    const idx = milestones.findIndex((m) => m.milestone_id === milestoneId);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx === -1 || swapIdx < 0 || swapIdx >= milestones.length) return;
    const reordered = [...milestones];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    setMilestones(reordered);
    setSavingId(milestoneId);
    try {
      await axios.post(
        `${API}/property-milestones/reorder`,
        { property_id: selectedPropertyId, milestone_ids: reordered.map((m) => m.milestone_id) },
        { params: agentIdParam, withCredentials: true }
      );
    } catch (e) {
      toast.error("Failed to reorder");
      load();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="milestones-module">
      <div className="milestones-module-header text-center space-y-2">
        <h2 className="text-3xl font-semibold" style={{ fontFamily: "var(--font-heading, inherit)" }}>
          {heading}
        </h2>
        {subheading && <p className="text-sm text-muted-foreground">{subheading}</p>}
      </div>

      {authState.loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !authState.signedIn ? (
        <div
          className="border border-border rounded-lg bg-muted/30 p-8 text-center space-y-3"
          data-testid="milestones-signin-gate"
        >
          <LayoutDashboard className="h-10 w-10 mx-auto text-primary" />
          <p className="text-lg font-medium">Sign in as an agent to view your dashboard</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            This dashboard is private to each agent — sign in to track milestones for your own properties.
          </p>
          <Button asChild data-testid="milestones-signin-btn" className="mt-2">
            <a href="/property-admin/login">
              <LogIn className="mr-2 h-4 w-4" /> Agent sign in
            </a>
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-border p-6" data-testid="milestones-module-content">
          <div className="flex items-start justify-between gap-4 mb-6 pb-4 border-b border-border flex-wrap">
            <div className="min-w-[220px]">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Home</Label>
              <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
                <SelectTrigger className="rounded-none mt-1" data-testid="milestones-property-select">
                  <SelectValue placeholder="Choose a home" />
                </SelectTrigger>
                <SelectContent>
                  {linkedProperties.map((p) => (
                    <SelectItem
                      key={p.property_id}
                      value={p.property_id}
                      data-testid={`milestones-property-option-${p.property_id}`}
                    >
                      {p.title || p.display_address || p.property_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="rounded-none"
              onClick={openAdd}
              disabled={!selectedPropertyId}
              data-testid="add-milestone-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Milestone
            </Button>
            {buyerContact && (
              <Button
                variant="outline"
                className="rounded-none border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setWhatsappOpen(true)}
                data-testid="milestones-whatsapp-buyer-btn"
              >
                <MessageCircle className="h-4 w-4 mr-1.5" /> Message buyer
              </Button>
            )}
          </div>

          {!linkedProperties.length ? (
            <div
              className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground"
              data-testid="milestones-empty-no-property"
            >
              You have no properties yet. Create a property to start tracking milestones.
            </div>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : milestones.length === 0 ? (
            <div
              className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground"
              data-testid="milestones-empty"
            >
              No milestones yet for {selectedProperty?.title || "this home"}. Click "Add Milestone" to get started.
            </div>
          ) : (
            <div className="space-y-3" data-testid="milestones-list">
              {milestones.map((m, idx) => {
                const colors = percentColor(m.percent_complete);
                const sm = statusMeta(m.status);
                const rowSaving = savingId === m.milestone_id;
                const required = isRequiredMilestone(m);
                const locked = !!requiredLockMap[m.milestone_id];

                if (locked) {
                  return (
                    <div
                      key={m.milestone_id}
                      className="border border-dashed border-red-300 bg-red-50/40 px-4 py-3 flex items-center gap-3"
                      data-testid={`milestone-row-${m.milestone_id}`}
                    >
                      <Lock className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-red-700 truncate" data-testid={`milestone-name-${m.milestone_id}`}>
                          {displayName(m.name)}
                        </p>
                        <p className="text-xs text-red-500" data-testid={`milestone-locked-${m.milestone_id}`}>
                          Locked — complete the required step above first
                        </p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={m.milestone_id}
                    className={`border p-4 ${required ? "border-red-400 ring-1 ring-red-200 bg-red-50/20" : "border-border"}`}
                    data-testid={`milestone-row-${m.milestone_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => toggleCompleted(m)}
                          disabled={rowSaving}
                          title={m.status === "completed" ? "Mark as not yet complete" : "Mark as completed (100%)"}
                          className={`mt-0.5 h-5 w-5 flex-shrink-0 rounded-sm border-2 flex items-center justify-center transition-colors ${
                            m.status === "completed"
                              ? "bg-emerald-500 border-emerald-500 text-white"
                              : "border-muted-foreground/40 hover:border-emerald-500 text-transparent"
                          }`}
                          data-testid={`milestone-complete-tick-${m.milestone_id}`}
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        </button>
                        <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-sm truncate" data-testid={`milestone-name-${m.milestone_id}`}>
                            {displayName(m.name)}
                          </h4>
                          {required && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold"
                              data-testid={`milestone-required-badge-${m.milestone_id}`}
                            >
                              <ShieldAlert className="h-2.5 w-2.5" /> Required
                            </span>
                          )}
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${sm.badge}`}
                            data-testid={`milestone-status-${m.milestone_id}`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${sm.dot}`} />
                            {sm.label}
                          </span>
                        </div>
                        {m.note && (
                          <p className="text-xs text-muted-foreground mt-1 truncate" data-testid={`milestone-note-${m.milestone_id}`}>
                            {m.note}
                          </p>
                        )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {rowSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-2" data-testid={`milestone-saving-${m.milestone_id}`} />
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={idx === 0}
                              onClick={() => moveMilestone(m.milestone_id, "up")}
                              data-testid={`move-up-milestone-${m.milestone_id}`}
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={idx === milestones.length - 1}
                              onClick={() => moveMilestone(m.milestone_id, "down")}
                              data-testid={`move-down-milestone-${m.milestone_id}`}
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEdit(m)}
                              data-testid={`edit-milestone-${m.milestone_id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700"
                              onClick={() => setDeleteTarget(m)}
                              data-testid={`delete-milestone-${m.milestone_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={m.percent_complete}
                        // Live-update the bar while dragging (no API call yet)...
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setMilestones((prev) =>
                            prev.map((x) => (x.milestone_id === m.milestone_id ? { ...x, percent_complete: val } : x))
                          );
                        }}
                        // ...and auto-save once the drag/keypress ends, no
                        // need to open Edit mode at all.
                        onMouseUp={(e) => patchMilestone(m.milestone_id, { percent_complete: Number(e.target.value) })}
                        onTouchEnd={(e) => patchMilestone(m.milestone_id, { percent_complete: Number(e.target.value) })}
                        onKeyUp={(e) => patchMilestone(m.milestone_id, { percent_complete: Number(e.target.value) })}
                        className="flex-1"
                        style={{ accentColor: colors.accent }}
                        data-testid={`milestone-percent-slider-${m.milestone_id}`}
                      />
                      <span className={`text-xs font-semibold w-10 text-right ${colors.text}`} data-testid={`milestone-percent-${m.milestone_id}`}>
                        {m.percent_complete}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="milestone-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-4 w-4" />
              {editingId ? "Edit Milestone" : "Add Milestone"}
            </DialogTitle>
            <DialogDescription>
              {selectedProperty?.title || selectedProperty?.display_address || "This home"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label>
              <Input
                className="rounded-none mt-1"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Mortgage offer received"
                maxLength={200}
                data-testid="milestone-name-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Tip: add a <code className="font-mono">*</code> to the name (e.g. "Obtain EPC*") to make it a{" "}
                <span className="font-medium text-red-600">required</span> step — later required steps stay locked until it's completed.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Percentage complete</Label>
                <span className={`text-xs font-semibold ${percentColor(form.percent_complete).text}`}>
                  {form.percent_complete}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={form.percent_complete}
                onChange={(e) => setForm((f) => ({ ...f, percent_complete: Number(e.target.value) }))}
                className="w-full mt-2"
                style={{ accentColor: percentColor(form.percent_complete).accent }}
                data-testid="milestone-percent-slider"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">One-line note</Label>
              <Input
                className="rounded-none mt-1"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="e.g. Awaiting buyer's solicitor"
                maxLength={300}
                data-testid="milestone-note-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="milestone-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`milestone-status-option-${s.value}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)} data-testid="milestone-dialog-cancel">
              Cancel
            </Button>
            <Button className="rounded-none" onClick={handleSave} disabled={saving} data-testid="milestone-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Save changes" : "Add Milestone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="milestone-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete milestone?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{deleteTarget?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="milestone-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="milestone-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WhatsAppUpdateModal
        open={whatsappOpen}
        onOpenChange={setWhatsappOpen}
        propertyId={selectedPropertyId}
        recipientNumber={buyerContact?.phone}
        recipientName={buyerContact?.name}
        defaultMessage={`Hi ${buyerContact?.name || "there"}, quick update on ${selectedProperty?.title || "your home"} — progress is moving along nicely.`}
      />
    </div>
  );
}
